/**
 * Security data hooks — dashboard, vulns, findings, scans, deps, pentests, posture
 */

import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api.js';

function usePolling<T>(path: string, intervalMs: number, enabled = true) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    if (!enabled) return;
    try {
      const result = await api.get<T>(path);
      setData(result);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Request failed');
    } finally {
      setLoading(false);
    }
  }, [path, enabled]);

  useEffect(() => {
    fetch();
    if (intervalMs <= 0 || !enabled) return;
    const timer = setInterval(fetch, intervalMs);
    return () => clearInterval(timer);
  }, [fetch, intervalMs, enabled]);

  return { data, loading, error, refresh: fetch };
}

export function useDashboard() {
  return usePolling<Record<string, unknown>>('/dashboard', 30_000);
}

export function useVulnerabilities(filters: Record<string, string> = {}) {
  const params = new URLSearchParams(filters).toString();
  return usePolling<{ vulnerabilities: unknown[]; total: number }>(
    `/vulnerabilities${params ? `?${params}` : ''}`, 30_000,
  );
}

export function useFindings(filters: Record<string, string> = {}) {
  const params = new URLSearchParams(filters).toString();
  return usePolling<{ findings: unknown[]; total: number }>(
    `/findings${params ? `?${params}` : ''}`, 30_000,
  );
}

export function useScans(filters: Record<string, string> = {}) {
  const params = new URLSearchParams(filters).toString();
  return usePolling<{ scans: unknown[]; total: number }>(
    `/scans${params ? `?${params}` : ''}`, 30_000,
  );
}

export function useDependencies(filters: Record<string, string> = {}) {
  const params = new URLSearchParams(filters).toString();
  return usePolling<{ dependencies: unknown[]; total: number }>(
    `/dependencies${params ? `?${params}` : ''}`, 60_000,
  );
}

export function usePentests(filters: Record<string, string> = {}) {
  const params = new URLSearchParams(filters).toString();
  return usePolling<{ pentests: unknown[]; total: number }>(
    `/pentests${params ? `?${params}` : ''}`, 30_000,
  );
}

export function usePentestFindings(runId: string | null) {
  return usePolling<{ findings: unknown[]; total: number }>(
    `/pentests?action=findings&run_id=${runId}`, 0, !!runId,
  );
}

export function usePostureReviews() {
  return usePolling<{ reviews: unknown[]; total: number }>('/posture', 60_000);
}

export function usePostureReview(date: string | null) {
  return usePolling<Record<string, unknown> | null>(
    `/posture?date=${date}`, 0, !!date,
  );
}

export function useSecurityConfig() {
  return usePolling<Record<string, unknown>>('/config', 60_000);
}

export function useOnboarding() {
  return usePolling<Record<string, unknown>>('/onboarding', 0);
}

export function useRunPentest() {
  const [loading, setLoading] = useState(false);

  const execute = useCallback(async (platform: string, agentType: string, targetUrl?: string) => {
    setLoading(true);
    try {
      return await api.post<{ run_id: string }>('/pentest/execute', { platform, agent_type: agentType, target_url: targetUrl });
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading };
}
